import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { String } from '@blade/std-lib'
import { Button, Swap2Icon, WalletFilledIcon } from '@blade/ui-kit'
import { walletsAtom } from '@root/entities/wallet/atoms'
import { WalletDistributeDialog } from '@root/features/wallet/distribute/ui/wallet-distribute-dialog'
import { BankIcon, DistributeIcon } from '@root/shared/ui-kit'

import { walletsManagerModelAtom } from '../../atoms'

type Props = {}

export const WalletsTableFooter: React.FC<Props> = () => {
  const { t } = useTranslation()
  const [model] = useAtom(walletsManagerModelAtom)
  const [selectedWallet] = useAtom(model.selectedWallet)
  const [selectedWallets] = useAtom(model.selectedWalletIds)
  const [wallets] = useAtom(walletsAtom)
  const [screen, setScreen] = useAtom(model.walletDetailsScreen)
  const [distributeOpen, setDistributeOpen] = useState(false)
  const selectedCount = selectedWallets.size

  const selectedLabel = `${selectedCount} ${t('wallet', { count: selectedCount })} ${String.toLowerCase(t('common.selected', { count: selectedCount }))}`

  const sourceWallet = useMemo(() => {
    const walletId = [...selectedWallets.values()].at(0)
    if (!walletId) {
      return undefined
    }
    return wallets.find((it) => it.id === walletId)
  }, [selectedWallets, wallets])

  return (
    <Root>
      <SelectionContainer>
        <SelectedCount>{selectedLabel}</SelectedCount>
        <SelectionButton
          data-disabled={selectedCount === 0}
          onClick={() => setDistributeOpen(true)}
        >
          <DistributeIcon size={12} />
          <div>{t('wallet.distribute')}</div>
        </SelectionButton>
      </SelectionContainer>
      <Actions>
        <ButtonStyled
          size="h36"
          radius="rounded"
          color="primary"
          iconLeft={<Swap2Icon />}
          disabled={!selectedWallet}
          data-active={selectedWallet && screen === 'swap'}
          onClick={() => setScreen('swap')}
        >
          {t('swap')}
        </ButtonStyled>
        <ButtonStyled
          size="h36"
          radius="rounded"
          color="primary"
          iconLeft={<WalletFilledIcon />}
          disabled={!selectedWallet}
          data-active={selectedWallet && screen === 'deposit'}
          onClick={() => setScreen('deposit')}
        >
          {t('deposit')}
        </ButtonStyled>
        <ButtonStyled
          size="h36"
          radius="rounded"
          variant="solid"
          color="primary"
          iconLeft={<BankIcon />}
          disabled={!selectedWallet}
          data-active={selectedWallet && screen === 'transfer'}
          onClick={() => setScreen('transfer')}
        >
          {t('transfer')}
        </ButtonStyled>
      </Actions>
      <AnimatePresence>
        {distributeOpen && selectedCount > 0 ? (
          <WalletDistributeDialog
            open
            onOpenChange={setDistributeOpen}
            sourceWallet={sourceWallet}
          />
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Root = styled.div`
  padding-block: 10px;
  padding-inline: 12px 10px;
  background: var(--background-1);
  border-top: 1px solid var(--background-3);

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: space-between;
`

const SelectionContainer = styled.div`
  display: grid;
  gap: 6px;
  justify-items: start;
`

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
`

const ButtonStyled = styled(Button)`
  /* &:disabled {
    color: var(--background-6);
    background: var(--background-2);
  }

  &[data-active='false'] {
    color: var(--background-12);
    background: var(--background-6);

    &:hover {
      background: var(--background-7);
    }
  } */
`

const SelectedCount = styled.div`
  color: var(--text-12);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
`

const SelectionButton = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;

  color: var(--text-8);

  &[data-disabled='false'] {
    cursor: pointer;
    color: var(--text-11);
    &:hover {
      color: var(--text-12);
    }
  }

  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`
